// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import getWindow from '../../../../utils/get-window'
import { Card, Field, FieldGroup, Label, Input, Select } from '../../../../atoms'
import Callout from '../../../callout'
import { lineOptions } from './line-options.ts'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const stringToFloat = (s) => (isNaN(parseFloat(s)) ? 0 : parseFloat(s))

const twoDecimalPlaces = (n) => (Number.isInteger(n) ? n : Math.round(n * 100) / 100)

const frequencyMap = {
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
}

const SavingsCalculator = ({
  params: {
    startingAmount: startingAmountParam,
    targetAmount: targetAmountParam,
    contribution: contributionParam,
    contributionFrequency: contributionFrequencyParam,
    interestRate: interestRateParam,
  },
  warningText,
}) => {
  const [startingAmount, setStartingAmount] = useState(startingAmountParam)
  const [targetAmount, setTargetAmount] = useState(targetAmountParam)
  const [contribution, setContribution] = useState(contributionParam)
  const [contributionFrequency, setContributionFrequency] = useState(contributionFrequencyParam)
  const [contributionString, setContributionString] = useState(contributionParam.toString())
  const [interestRate, setInterestRate] = useState(interestRateParam)
  const [savingTime, setSavingTime] = useState(null)
  const [interestSaved, setInterestSaved] = useState(null)
  const [chartData, setChartData] = useState(null)

  useEffect(() => {
    setContribution(parseFloat(contributionString))
  }, [contributionString])

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(getWindow().location?.search)
    const entries = Object.fromEntries(urlSearchParams.entries())
    const entriesParsed = Object.entries(entries).reduce(
      (obj, [key, val]) => ({ ...obj, [key]: parseInt(val) }),
      {},
    )
    if (entriesParsed.startingAmount) {
      setStartingAmount(entriesParsed.startingAmount)
    }
    if (entriesParsed.targetAmount) {
      setTargetAmount(entriesParsed.targetAmount)
    }
    if (entriesParsed.contributionString) {
      setContributionString(entriesParsed.contributionString)
    }
    if (entriesParsed.contributionFrequency) {
      setContributionFrequency(entriesParsed.contributionFrequency)
    }
    if (entriesParsed.interestRate) {
      setInterestRate(entriesParsed.interestRate)
    }
  }, [])

  useEffect(() => {
    const savingsData = []
    let amount = startingAmount
    let interestSavedTotal = 0
    let steps = 0
    while (targetAmount - 0.0000001 > amount && steps < 200) {
      steps += 1
      savingsData.push(amount)
      if (contributionFrequency === 'weekly') {
        interestSavedTotal += amount * ((1 + interestRate / 100) ** (1 / 52) - 1)
        amount *= (1 + interestRate / 100) ** (1 / 52)
      } else if (contributionFrequency === 'monthly') {
        interestSavedTotal += amount * ((1 + interestRate / 100) ** (1 / 12) - 1)
        amount *= (1 + interestRate / 100) ** (1 / 12)
      } else {
        interestSavedTotal += (amount * interestRate) / 100
        amount *= 1 + interestRate / 100
      }
      amount += contribution
    }
    savingsData.push(amount)
    setInterestSaved(interestSavedTotal)
    if (contributionFrequency === 'monthly' && steps >= 12) {
      let s = `${Math.floor(steps / 12)} year${steps === 12 ? '' : 's'}`
      if (steps % 12 !== 0) {
        s += ` ${steps % 12} month${steps % 12 === 1 ? '' : 's'}`
      }
      setSavingTime(s)
    } else {
      setSavingTime(`${steps} ${frequencyMap[contributionFrequency]}${steps === 1 ? '' : 's'}`)
    }
    setChartData({ steps: savingsData.map((_, i) => i), savingsData })
  }, [startingAmount, targetAmount, contribution, contributionFrequency, interestRate])

  return (
    <div className='SavingsCalculator'>
      <Card variant='plum'>
        <Field stack={true}>
          <Label>Starting amount/ How much do you have now?</Label>
          <Input
            value={startingAmount}
            onChange={(e) => {
              setStartingAmount(e.target.value)
            }}
            type={'text'}
            prefix={'£'}
            format={'thousands'}
          />
        </Field>
        <Field stack={true}>
          <Label>Target amount/ How much are you looking to save?</Label>
          <Input
            value={targetAmount}
            onChange={(e) => {
              setTargetAmount(e.target.value)
            }}
            type={'text'}
            prefix={'£'}
            format={'thousands'}
          />
        </Field>
        <Field>
          <Label>How much can you afford to save?</Label>
          <FieldGroup>
            <Input
              value={contributionString}
              onChange={(e) => {
                setContributionString(e.target.value)
              }}
              type={'text'}
              prefix={'£'}
              format={'thousands'}
            />
            <Select
              name='contribution-frequency'
              value={contributionFrequency}
              options={[
                { value: 'weekly', text: 'Weekly' },
                { value: 'monthly', text: 'Monthly' },
                { value: 'yearly', text: 'Yearly' },
              ]}
              onChange={(e) => {
                setContributionFrequency(e.target.value)
              }}
            />
          </FieldGroup>
        </Field>

        <Field stack={true}>
          <Label>Interest rate (%)</Label>
          <Input
            value={interestRate}
            onChange={(e) => setInterestRate(stringToFloat(e.target.value))}
            type={'text'}
          />
        </Field>

        {savingTime !== null && interestSaved !== null && chartData !== null && (
          <div className='savings-chart'>
            <h3>
              It should take you <span>{savingTime}</span> to save your goal amount.
            </h3>
            <h3>
              At an interest rate of <span>{interestRate}%</span> a year, you will earn{' '}
              <span>£{twoDecimalPlaces(interestSaved)}</span> of interest.
            </h3>
            <div className='savings-chart-graph'>
              <Line
                options={lineOptions([])}
                data={{
                  labels: chartData.steps.map(
                    (s) => `${s} ${frequencyMap[contributionFrequency]}${s === 1 ? '' : 's'}`,
                  ),
                  datasets: [
                    {
                      label: 'Savings',
                      data: chartData.savingsData,
                      borderColor: '#DB4D75',
                      backgroundColor: '#DB4D75',
                      spanGaps: true,
                    },
                  ],
                }}
              />
            </div>
          </div>
        )}
      </Card>
      {warningText && <Callout variant='platinum-warning' bold={true} text={warningText} />}
    </div>
  )
}

export default SavingsCalculator
